<template>
    <div class="mb-8">

        <div class="mx-auto">

            <div class="d-flex flex-wrap justify-space-between mx-5 mt-5 mb-5">

                <div class="font-weight-bold text-h3">Projects</div>

                <!-- Add project button -->
                <div><v-btn @click="addProject = true" color="indigo-darken-4 rounded-xl" size="large">Add
                        new project</v-btn></div>

                <!-- Add project popup -->
                <v-dialog v-model="addProject" max-width="600px" @click:outside="resetClientFields">
                    <v-card>
                        <v-card-title>
                            <span class="headline">Add Client</span>
                        </v-card-title>
                        <v-card-text>
                            <v-form @submit.prevent="addClient">

                                <!-- Client ID -->
                                <v-subheader>Client Information</v-subheader>
                                <v-text-field v-model="newClient.clientId" label="Client ID" required></v-text-field>

                                <!-- Firebase Config -->
                                <v-divider></v-divider>
                                <v-subheader>Firebase Configuration</v-subheader>
                                <v-text-field v-model="newClient.firebaseConfig.apiKey"
                                    label="Firebase API Key"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.appId"
                                    label="Firebase App ID"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.authDomain"
                                    label="Firebase Auth Domain"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.databaseUrl"
                                    label="Firebase Database URL"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.messagingSenderId"
                                    label="Firebase Messaging Sender ID"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.projectId"
                                    label="Firebase Project ID"></v-text-field>
                                <v-text-field v-model="newClient.firebaseConfig.storageBucket"
                                    label="Firebase Storage Bucket"></v-text-field>

                                <!-- Firebase Region -->
                                <v-divider></v-divider>
                                <v-subheader>Firebase Region</v-subheader>
                                <v-text-field v-model="newClient.firebaseRegion" label="Firebase Region"></v-text-field>

                                <!-- Functions -->
                                <v-divider></v-divider>
                                <v-subheader>Functions</v-subheader>
                                <v-text-field v-model="newClient.functions.usapi"
                                    label="Functions USAPI"></v-text-field>

                                <!-- Localhost -->
                                <v-divider></v-divider>
                                <v-subheader>Localhost</v-subheader>
                                <v-text-field v-model="newClient.localhost" label="Localhost"></v-text-field>

                                <!-- Model Bucket -->
                                <v-divider></v-divider>
                                <v-subheader>Model Bucket</v-subheader>
                                <v-text-field v-model="newClient.modelBucket" label="Model Bucket"></v-text-field>

                                <!-- Node Config -->
                                <v-divider></v-divider>
                                <v-subheader>Node Configuration</v-subheader>
                                <v-text-field v-model="newClient.nodeConfig.host"
                                    label="Node Config Host"></v-text-field>

                                <!-- ZeroTier -->
                                <v-divider></v-divider>
                                <v-subheader>ZeroTier</v-subheader>
                                <v-text-field v-model="newClient.zerotier" label="ZeroTier"></v-text-field>

                                <!-- Active -->
                                <v-divider></v-divider>
                                <v-subheader>Active</v-subheader>
                                <v-switch v-model="newClient.active" label="Active"></v-switch>

                                <!-- Logo -->
                                <v-divider></v-divider>
                                <v-subheader>Logo</v-subheader>
                                <v-container class="pa-0">
                                    <v-row justify="center">
                                        <v-col cols="12" class="text-center">
                                            <v-file-input v-if="!newClient.logo" accept="image/*"
                                                label="Drop an image or click to upload" @change="handleFileUpload"
                                                prepend-icon></v-file-input>

                                            <div v-else class="mt-3" style="position: relative;">
                                                <v-img :src="`data:image/png;base64,${newClient.logo}`"
                                                    alt="Logo preview" width="200" cover />
                                                <v-btn icon small @click="newClient.logo = ''"
                                                    style="position:absolute; top:50%;right:0; transform: translateY(-50%);">
                                                    <v-icon color="black" icon="fa-regular fa-x"
                                                        style="font-size: 16px"></v-icon>
                                                </v-btn>
                                            </div>

                                        </v-col>
                                    </v-row>
                                </v-container>

                            </v-form>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn color="indigo darken-4" text
                                @click="() => { addProject = false; resetClientFields() }">Cancel</v-btn>
                            <v-btn color="indigo darken-4" text @click="() => { addClient(); resetClientFields() }">Add
                                Client</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

            </div>

            <hr>

            <!-- Filters -->
            <div class="d-flex flex-column mt-5">

                <!-- Search project -->
                <div class="d-flex justify-space-between mx-5">
                    <div>
                        <v-text-field variant="solo-filled" style="width: 280px" label="Search project"
                            v-model="searchQuery" density="comfortable" rounded></v-text-field>
                    </div>

                    <!-- Order By -->
                    <div>
                        <v-select variant="solo-filled" v-model="sortOption" :items="sortOptions" label="Order By"
                            item-title="text" item-value="value" style="width: 280px" density="comfortable"
                            rounded></v-select>
                    </div>
                </div>

                <div class="d-flex flex-wrap justify-space-between mx-5">
                    <CardCmp v-for="project in filteredProjects" :key="project.id" :project="project"
                        :deleteProject="deleteProject" class="mb-5" />
                </div>
            </div>



        </div>

    </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";

import CardCmp from "@/components/card/Main.vue";

import * as rosepetalModel from "rosepetal-model";

let $meta = rosepetalModel.default.meta;

const projects = ref([]);
const addProject = ref(false);
const sortOption = ref("__name__-asc");
const searchQuery = ref("");

//Format: documentField - asc|desc
const sortOptions = [
    {
        text: 'Project Name (Ascending)',
        value: '__name__-asc'
    },
    {
        text: 'Project Name (Descending)',
        value: '__name__-desc'
    },
    {
        text: 'Creation Date (Ascending)',
        value: 'createdAt-asc'
    },
    {
        text: 'Creation Date (Descending)',
        value: 'createdAt-desc'
    },
    {
        text: 'Updated (Ascending)',
        value: 'updatedAt-asc'
    },
    {
        text: 'Updated (Descending)',
        value: 'updatedAt-desc'
    },

];

const newClient = ref({
    clientId: "",
    firebaseConfig: {
        apiKey: "",
        appId: "",
        authDomain: "",
        databaseUrl: "",
        messagingSenderId: "",
        projectId: "",
        storageBucket: ""
    },
    firebaseRegion: "",
    functions: {
        usapi: ""
    },
    localhost: "",
    modelBucket: "",
    nodeConfig: {
        host: ""
    },
    zerotier: "",
    active: true,
    logo: ""
});

const filteredProjects = computed(() => {
    return projects.value.filter(project =>
        project.id.toLowerCase().includes(searchQuery.value.toLowerCase())
    );
});

function getNumericTime(project, date) {
    return project.metadata[date] === "" ? 0 : project.metadata[date].toMillis();
}

function sortProjects() {
    const [sortBy, sortOrder] = sortOption.value.split("-");
    const isAsc = sortOrder === "asc";

    projects.value.sort((a, b) => {
        if (sortBy === "__name__") {
            return isAsc ? a.id.localeCompare(b.id) : b.id.localeCompare(a.id);
        }

        const dateA = getNumericTime(a, sortBy);
        const dateB = getNumericTime(b, sortBy);

        return isAsc ? dateA - dateB : dateB - dateA;
    });
}


async function addClient() {
    try {
        await $meta.addClient(newClient.value);
        await getProjects();
        addProject.value = false;
    } catch (error) {
        console.error("Error adding client: ");
    }
}


async function getProjects() {
    try {
        const projectsDocs = await $meta.getProjects();

        projects.value = projectsDocs.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            servers: [],
            isProjectOnline: false,
        }));
        await getMetadata();

        const zerotier = await getZerotier();

        projects.value.forEach(async (project) => {

            // assign zerotier servers to project   
            zerotier.forEach((server) => {
                if (server.name.includes(project.id)) {
                    project.servers.push(server)
                }
            })

            // get the global zerotier online status 
            project.isProjectOnline = project.servers.some(server => server.isOnline);
            
        });

    } catch (error) {
        console.log("Error getting projects: ");
    }
}

async function getMetadata() {
    try {
        const metadataDocs = await $meta.getMetadata();

        metadataDocs.docs.forEach((doc) => {
            const metadata = {
                id: doc.id,
                ...doc.data(),
            };

            const project = projects.value.find((project) => project.id === metadata.id);
            if (project) {
                project.metadata = metadata;
            }
        });
    }
    catch (error) {
        console.log("Error getting metadata: ");
    }
}

function isServerOnline(lastConnection) {
    const now = new Date();
    const difference = (now - lastConnection) / 60_000;
    return difference < 3;
}

async function getZerotier() {
    try {
        const peers = await $meta.getPeers();
        const zerotierInfo = [];

        projects.value.forEach((project) => {
            const matchingServers = peers.filter((server) => server.name.includes(project.id));
            matchingServers.forEach((server) => {
                // Fix for last connection mismatch
                const lastConnectionDate = new Date(server.lastConnection);
                lastConnectionDate.setHours(lastConnectionDate.getHours() + 2);

                const matchingServer = {
                    name: server.name,
                    projectId: project.id,
                    ip: server.ip,
                    lastConnection: lastConnectionDate.toLocaleString(),
                    isOnline: isServerOnline(lastConnectionDate)
                };
                zerotierInfo.push(matchingServer);
            });
        });

        const zerotier = await Promise.all(zerotierInfo);
        return zerotier;

    } catch (error) {
        console.log("Error getting the zerotier info: ", error);
    }
}

async function deleteProject(projectId) {
    try {
        await $meta.deleteProject(projectId);
    } catch (error) {
        console.log("Error deleting project: ", error);
    }
}

function resetClientFields() {
    newClient.value = {
        clientId: "",
        firebaseConfig: {
            apiKey: "",
            appId: "",
            authDomain: "",
            databaseUrl: "",
            messagingSenderId: "",
            projectId: "",
            storageBucket: ""
        },
        firebaseRegion: "",
        functions: {
            usapi: ""
        },
        localhost: "",
        modelBucket: "",
        nodeConfig: {
            host: ""
        },
        zerotier: "",
        active: true,
        logo: ""
    };
}

function handleFileUpload(event) {
    const file = event.target.files[0];

    if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            newClient.value.logo = e.target.result.split(',')[1];
        }
        reader.readAsDataURL(file);
    }
    else {
        console.error("File error")
    }
}

onMounted(async () => {
    try {
        await getProjects();
    } catch (error) {
        console.log("Error getting projects: ");
    }
});

watch(sortOption, () => {
    sortProjects();
});

</script>

<style></style>